import { useEffect, useState } from "react";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import {
  CheckboxComponent,
  TextFieldComponent,
  SelectFieldComponent,
} from "../external/ensolvers-core-frontend-mui/components/Common/FormComponents";
import { traceo } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import { useTranslation } from "react-i18next";
import Project from "../models/Project";
import { MOBILE_WIDTH, ROWS_PER_PAGE } from "../App";
import { AppToolbar } from "../components/AppToolbar";
import RoleUtils from "../utils/RoleUtils";
import { AppDialog } from "../components/AppDialog";
import { EnsolversOpsUser } from "../models/EnsolversOpsUser";
import { Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Box, Grid } from "@mui/material";
import { ModalAddRemovePM } from "../components/ModalAddRemovePM";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import { BooleanSegmented } from "../components/SegmentedControl";
import { SelectComponent } from "../components/SelectComponent";
import { BillingPolicy } from "../types/BillingPolicy";
import useTableHeight from "../hooks/useTableHeight";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";

import ActionButton, { ActionsMenuEntryIcons } from "../components/Worklog/ActionButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { EditDialog } from "../external/ensolvers-core-frontend-mui/components/EditDialog";
import { DialogPosition } from "../external/ensolvers-core-frontend-mui/components/Common/DialogPosition";
import { customButtonBuilder, customButtonsContainer, gridButtonContainer } from "../components/Main/tableStyles";
import { useToolbarActions } from "../components/Main/ToolbarActions";
import { useStyles as formStyles } from "../components/Main/formStyles";
import { filtersBarStyle, Token } from "../theme";
import { Theme } from "@mui/material/styles";
import useProjectList, { ProjectType } from "../hooks/useProjectList";

function AssignedManagerCellComponent(props: {
  projectExternalId: string;
  forceUpdateOn?: number;
  getManagerList: (id: string) => Promise<EnsolversOpsUser[]>;
}): JSX.Element {
  const defaultStringToShow: string = "-";
  const [managerListToShow, setManagerListToShow] = useState<string>(defaultStringToShow);

  useEffect(() => {
    props
      .getManagerList(props.projectExternalId)
      .then((managerList) => {
        if (!managerList || managerList.length === 0) {
          setManagerListToShow(defaultStringToShow);
        } else {
          setManagerListToShow(managerList.map((value) => value.firstName + " " + value.lastName.slice(0, 1)).join(", "));
        }
      })
      .catch((error) => traceo("Error getting manager list from project.", error, null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.projectExternalId, props.forceUpdateOn]);

  return <>{managerListToShow}</>;
}

export default function ProjectList() {
  const { toolbarActionsSlot, addButtonInToolbar } = useToolbarActions();
  const { t } = useTranslation();
  const classes = useStyles();
  const customStyles = formStyles();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  const {
    onDataFetch,
    forceUpdateOn,
    isLoading,
    projectType,
    handleChangeProjectType,
    assigned,
    handleChangeAssignedProjectsByUser,
    showArchivedProjects,
    toggleShowArchivedProjects,
    selectedProject,
    setSelectedProject,
    selectedEditProject,
    editProjectManager,
    isEditing,
    setIsEditing,
    managerList,
    setManagerList,
    searchManager,
    setSearchManager,
    userNames,
    handleCloseManagerModal,
    handleAddManager,
    handleRemoveManager,
    isUpdatingManagers,
    handleOnCancel,
    getManagerListFromProject,
    handleOnDeleteProject,
    onCreateProject,
    onUpdateProject,
    handleOpenAssignPM,
    handleOpenDeleteDialog,
    handleOpenEditDialog,
  } = useProjectList();

  const getAdditionalActions = (instance: Project): ActionsMenuEntryIcons[] => {
    const options = RoleUtils.isAdmin()
      ? [
          {
            title: t("Assign PM"),
            action: () => handleOpenAssignPM(instance),
            icon: <PersonOutlineIcon />,
          },
          {
            title: t("Delete"),
            action: () => handleOpenDeleteDialog(instance),
            icon: <DeleteOutlineIcon />,
          },
          {
            title: t("Edit"),
            action: () => handleOpenEditDialog(instance),
            icon: <EditIcon />,
          },
        ]
      : [];

    return options;
  };

  return (
    <AppContainerFullHeight
      bottomSectionRef={bottomSectionRef}
      topSection={
        <>
          <AppToolbar title="Projects" rightContent={toolbarActionsSlot}>
            <div className={classes.container}>
              <SelectComponent
                label={"Project Type"}
                value={projectType}
                setValue={handleChangeProjectType}
                options={Object.values(ProjectType)}
                size="small"
              />
              <div>
                <BooleanSegmented
                  checked={assigned}
                  onToggle={handleChangeAssignedProjectsByUser}
                  offLabel="All"
                  onLabel="Mine"
                  label="Assigned"
                  ariaLabel="Assigned to me"
                />
                <BooleanSegmented
                  checked={showArchivedProjects}
                  onToggle={toggleShowArchivedProjects}
                  offLabel="Active"
                  onLabel="Archived"
                  label="Status"
                  ariaLabel="Archived projects"
                />
              </div>
            </div>
          </AppToolbar>
        </>
      }
      bottomSection={
        <Box sx={{ borderRadius: 8 }}>
          <SpinnerWrapper isLoading={isLoading}>
            <TableBasedCrud<Project>
              headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
              buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
              cancelButtonStyles={{
                color: Token.textSecondary,
                border: `1px solid ${Token.borderDefault}`,
                marginBottom: 10,
              }}
              dialogStyles={customStyles.root}
              buttonContainer={customButtonsContainer}
              gridButtonContainer={gridButtonContainer}
              addButtonBuilder={addButtonInToolbar(customButtonBuilder)}
              maxHeight={calculateMaxHeight(RoleUtils.isAdmin())}
              stickyHeader
              stickyPagination
              elementName={"Project"}
              rowsPerPage={ROWS_PER_PAGE}
              disableAutoFetch
              forceUpdateOn={forceUpdateOn}
              enablePagination
              denseTable
              stylesForRowIfCondition={(instance) => (instance.archived ? { backgroundColor: Token.surfaceSunken } : {})}
              onDataFetch={onDataFetch}
              onCreate={onCreateProject}
              onUpdate={onUpdateProject}
              onCancelDialog={handleOnCancel}
              onClickEdit={() => setIsEditing(true)}
              renderDialogContent={(register, errors, control) => (
                <>
                  <TextFieldComponent propertyName={"name"} label="Name" required={true} />
                  <TextFieldComponent
                    propertyName={"key"}
                    label="Key"
                    required={true}
                    pattern={/^[A-Za-z0-9_]+$/}
                    customHelperText={"Key should be provided. Valid characters: A-Z a-z 0-9 _"}
                  />
                  <TextFieldComponent propertyName={"slackChannelId"} label="Internal Slack Channel Id" required={false} />
                  <TextFieldComponent propertyName={"externalSlackChannelId"} label="External Slack Channel Id" required={false} />

                  <SelectFieldComponent
                    control={control}
                    register={register}
                    errors={errors}
                    propertyName={"defaultBillingPolicy"}
                    label="Default Billing Policy"
                    required={false}
                    options={[
                      { value: BillingPolicy[BillingPolicy.SUM_HOURS], label: BillingPolicy.SUM_HOURS.toString() },
                      {
                        value: BillingPolicy[BillingPolicy.SUM_HOURS_WITH_MINIMUM],
                        label: BillingPolicy.SUM_HOURS_WITH_MINIMUM.toString(),
                      },
                    ]}
                  />

                  <Grid container display={"flex"} alignItems={"center"} justifyItems={"center"}>
                    <Grid item xs={12} sm={6}>
                      <CheckboxComponent
                        control={control}
                        register={register}
                        errors={errors}
                        propertyName={"omitHoursLimit"}
                        label={"Omit Hours Limit"}
                        required={false}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <CheckboxComponent
                        control={control}
                        register={register}
                        errors={errors}
                        propertyName={"internal"}
                        label={"Internal"}
                        required={false}
                      />
                    </Grid>
                    {isEditing && (
                      <Grid item xs={12} sm={6}>
                        <CheckboxComponent
                          control={control}
                          register={register}
                          errors={errors}
                          propertyName={"archived"}
                          label={"Archived"}
                          required={false}
                        />
                      </Grid>
                    )}
                  </Grid>
                </>
              )}
              tableColumns={[
                {
                  header: "Name",
                  content: (instance) => <>{instance.name}</>,
                },
                {
                  header: "Key",
                  content: (instance) => <>{instance.key}</>,
                },
                { header: "Internal Slack Channel Id", content: (instance) => <>{instance.slackChannelId}</> },
                {
                  header: "External Slack Channel Id",
                  content: (instance) => <>{instance.externalSlackChannelId}</>,
                },
                {
                  header: "Omit hours limit",
                  content: (instance) => <>{instance.omitHoursLimit ? "Yes" : "No"}</>,
                },
                {
                  header: "Internal",
                  content: (instance) => <>{instance.internal ? "Yes" : "No"}</>,
                },
                {
                  header: "Archived",
                  content: (instance) => <>{instance.archived ? "Yes" : "No"}</>,
                },
                {
                  header: "Assigned Managers",
                  content: (instance) => (
                    <AssignedManagerCellComponent
                      projectExternalId={instance.externalId || ""}
                      forceUpdateOn={forceUpdateOn}
                      getManagerList={getManagerListFromProject}
                    />
                  ),
                },
                {
                  header: "Default Billing Policy",
                  content: (instance) => <>{instance.defaultBillingPolicy}</>,
                },
                {
                  header: t("Actions"),
                  content: (instance) => {
                    const actions = getAdditionalActions(instance);
                    return (
                      <>
                        {actions.map((action) => {
                          return <ActionButton onClick={action.action} tooltip={action.title} icon={action.icon} />;
                        })}
                      </>
                    );
                  },
                },
              ]}
              hideActions={true} // Hide default Actions column
              buildAdditionalActions={
                RoleUtils.isAdmin()
                  ? (instance, refresh) => {
                      return [
                        {
                          title: t("Assign PM"),
                          action: () => handleOpenAssignPM(instance),
                        },
                        {
                          title: t("Delete"),
                          action: () => handleOpenDeleteDialog(instance),
                        },
                      ];
                    }
                  : undefined
              }
              resetValue={{
                name: "",
                key: "",
                slackChannelId: "",
                externalSlackChannelId: "",
                phone: "",
                omitHoursLimit: false,
                defaultBillingPolicy: "",
              }}
            />
          </SpinnerWrapper>
          {/* Dialog to delete projects */}
          <AppDialog
            open={!!selectedProject}
            onClose={() => {
              setSelectedProject(undefined);
              setManagerList(undefined);
            }}
            title={"Delete"}
            content={
              <>
                <Typography>{`The next project is going to be eliminated: ${selectedProject?.name}.`}</Typography>
                {managerList?.length > 0 && (
                  <>
                    <Typography>The following managers associated with this project will also be removed:</Typography>

                    <Box component="ul" sx={{ my: 2, pl: 2 }}>
                      {managerList?.map((manager, index) => (
                        <ul key={index}>
                          <Typography>{`${manager.firstName} ${manager.lastName}`}</Typography>
                        </ul>
                      ))}
                    </Box>
                  </>
                )}

                <Typography>{`Do you want to confirm this action?`}</Typography>
              </>
            }
            buttons={[{ label: "Confirm delete", onClick: () => handleOnDeleteProject() }]}
          />
          {/* Dialog to list, add and remove assigned Project Managers */}
          <ModalAddRemovePM
            editProjectManager={editProjectManager}
            handleCloseManagerModal={handleCloseManagerModal}
            managerList={managerList}
            onAddManager={handleAddManager}
            onRemoveManager={handleRemoveManager}
            isUpdating={isUpdatingManagers}
            setSearchManager={setSearchManager}
            searchManager={searchManager}
            userNames={userNames}
          />
          {/* Copy of the Dialog to Edit -- To use with Edit Action Icon */}
          <EditDialog<Project>
            headerStyles={{ color: Token.textPrimary, marginBottom: -10, marginTop: 10 }}
            buttonStyles={{ marginRight: 20, color: "white", backgroundColor: Token.actionPrimary, marginBottom: 10 }}
            cancelButtonStyles={{
              color: Token.textSecondary,
              border: `1px solid ${Token.borderDefault}`,
              marginBottom: 10,
            }}
            dialogStyles={customStyles.root}
            elementName={"Project"}
            dialogPosition={DialogPosition.Center}
            currentInstance={selectedEditProject}
            newItemDialogOpen={isEditing}
            onUpdate={onUpdateProject}
            onCreate={onCreateProject}
            onCancel={handleOnCancel}
            renderDialogContent={(register, errors, control) => (
              <>
                <TextFieldComponent propertyName={"name"} label="Name" required={true} />
                <TextFieldComponent
                  propertyName={"key"}
                  label="Key"
                  required={true}
                  pattern={/^[A-Za-z0-9_]+$/}
                  customHelperText={"Key should be provided. Valid characters: A-Z a-z 0-9 _"}
                />
                <TextFieldComponent propertyName={"slackChannelId"} label="Internal Slack Channel Id" required={false} />
                <TextFieldComponent propertyName={"externalSlackChannelId"} label="External Slack Channel Id" required={false} />

                <SelectFieldComponent
                  control={control}
                  register={register}
                  errors={errors}
                  propertyName={"defaultBillingPolicy"}
                  label="Default Billing Policy"
                  required={false}
                  options={[
                    { value: BillingPolicy[BillingPolicy.SUM_HOURS], label: BillingPolicy.SUM_HOURS.toString() },
                    { value: BillingPolicy[BillingPolicy.SUM_HOURS_WITH_MINIMUM], label: BillingPolicy.SUM_HOURS_WITH_MINIMUM.toString() },
                  ]}
                />

                <Grid container display={"flex"} alignItems={"center"} justifyItems={"center"}>
                  <Grid item xs={12} sm={6}>
                    <CheckboxComponent
                      control={control}
                      register={register}
                      errors={errors}
                      propertyName={"omitHoursLimit"}
                      label={"Omit Hours Limit"}
                      required={false}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CheckboxComponent
                      control={control}
                      register={register}
                      errors={errors}
                      propertyName={"internal"}
                      label={"Internal"}
                      required={false}
                    />
                  </Grid>
                  {isEditing && (
                    <Grid item xs={12} sm={6}>
                      <CheckboxComponent
                        control={control}
                        register={register}
                        errors={errors}
                        propertyName={"archived"}
                        label={"Archived"}
                        required={false}
                      />
                    </Grid>
                  )}
                </Grid>
              </>
            )}
            setNewInstanceDialogOpen={() => {}}
            setLastUpdateTimestamp={() => {}}
          />
        </Box>
      }
    />
  );
}

const useStyles = makeStyles<Theme, {}, string>(
  // @ts-ignore -- computed media-query keys are valid JSS but not StyleRules
  (theme) => ({
    container: {
      ...filtersBarStyle(),
      "& > div:last-child": {
        display: "flex",
        flexDirection: "row",
        columnGap: "10px",
      },
      "& > div:first-child": {
        margin: "0 !important",
      },
    },
    [theme.breakpoints.down(MOBILE_WIDTH)]: {
      container: {
        flexDirection: "column",
        rowGap: "10px",
      },
    },
  }),
  { index: 1 },
);
